import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import EventIcon from '@material-ui/icons/Event';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import PieChartIcon from '@material-ui/icons/PieChart';
import BarChartIcon from '@material-ui/icons/BarChart';
import type { Moment } from 'moment';
import { Button } from '@this/shared/ui/inputs/button';
import { Input } from '@this/shared/ui/inputs/input';
import type CancelLimit from '@this/domain/cancel_limit2';
import { observer } from 'mobx-react';
import DatetimePicker from '../../../../../shared/datetime_picker/datetime_picker';

interface Props {
  cancelLimit: CancelLimit;
  index: number;
  checkinDate: Moment;
  classNameForModifiedField: (...path: (string | number)[]) => string;
  onRemove: (cancelLimit: CancelLimit) => void;
}

const CancelLimitFormItemComponent = ({
  cancelLimit,
  index,
  checkinDate,
  classNameForModifiedField,
  onRemove
}: Props) => {
  const limitDatetimeMode = cancelLimit.limitDatetimeMode;
  const cancelFeeMode = cancelLimit.cancelFeeMode;

  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
      <CancelIconArea>
        <Tooltip
          title={limitDatetimeMode === 'daysBefore' ? 'カレンダー選択に切り替え' : '日数選択に切り替え'}
          placement="top"
          arrow
        >
          <IconButton
            size="small"
            color="primary"
            onClick={() =>
              cancelLimit.setLimitDatetimeMode(limitDatetimeMode === 'daysBefore' ? 'calendar' : 'daysBefore')
            }
            style={{ padding: '6px' }}
          >
            {limitDatetimeMode === 'daysBefore' ? (
              <EventIcon fontSize="small" />
            ) : (
              <BorderColorIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </CancelIconArea>
      {limitDatetimeMode === 'daysBefore' ? (
        <InputArea className={classNameForModifiedField('cancelLimits', index, 'limitDatetime')}>
          <TextField
            type="number"
            min={0}
            value={cancelLimit.daysBefore(checkinDate)}
            onChange={e => {
              const value = parseInt(e.target.value, 10);
              cancelLimit.setDaysBefore(value, checkinDate);
            }}
          />
          <EndAdornment>日前</EndAdornment>
        </InputArea>
      ) : (
        <DatepickerArea className={classNameForModifiedField('cancelLimits', index, 'limitDatetime')}>
          <DatetimePicker
            value={cancelLimit.limitDatetime}
            onChange={date => cancelLimit.setLimitDatetime(date)}
            showTime
            showPast
          />
        </DatepickerArea>
      )}
      <CancelIconArea>
        <Tooltip
          title={cancelFeeMode === 'fixed' ? '割合料金に切り替え' : '固定料金に切り替え'}
          placement="top"
          arrow
        >
          <IconButton
            size="small"
            color="primary"
            onClick={() => cancelLimit.setCancelFeeMode(cancelFeeMode === 'fixed' ? 'percent' : 'fixed')}
            style={{ padding: '6px' }}
          >
            {cancelFeeMode === 'fixed' ? <PieChartIcon fontSize="small" /> : <BarChartIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </CancelIconArea>
      {cancelFeeMode === 'fixed' ? (
        <InputArea className={classNameForModifiedField('cancelLimits', index, 'cancelFeeAmount')}>
          <TextField
            key="cancelFeeAmount"
            type="number"
            min={0}
            value={cancelLimit.cancelFeeAmount || 0}
            onChange={e => {
              const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
              if (isNaN(value) || value < 0) return;

              cancelLimit.setCancelFeeAmount(value);
            }}
            fee
          />
          <EndAdornment>円</EndAdornment>
        </InputArea>
      ) : (
        <InputArea className={classNameForModifiedField('cancelLimits', index, 'cancelFeePercent')}>
          <TextField
            key="cancelFeePercent"
            type="number"
            min={0}
            max={100}
            value={cancelLimit.cancelFeePercent}
            onChange={e => {
              const value = parseInt(e.target.value, 10);
              cancelLimit.setCancelFeePercent(value);
            }}
            fee
          />
          <EndAdornment>%</EndAdornment>
        </InputArea>
      )}
      <Button
        onClick={() => onRemove(cancelLimit)}
        style={{
          minWidth: '20px',
          maxWidth: '20px',
          padding: 0,
          height: '20px',
          borderRadius: '10px'
        }}
      >
        ×
      </Button>
    </div>
  );
};

const CancelIconArea: React.FC = ({ children }) => <div style={{ display: 'flex', gap: '4px' }}>{children}</div>;

const inputAreaStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '5px',
    '& input': {
      margin: '0 !important'
    }
  },
  datepicker: {
    '& > div': {
      display: 'flex',
      alignItems: 'center'
    },
    '& .ant-calendar-picker-input': {
      width: '105px !important'
    }
  }
});

const InputArea: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className }) => {
  const classes = inputAreaStyles();

  return <div className={`${classes.root} ${className}`}>{children}</div>;
};

const DatepickerArea: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className }) => {
  const classes = inputAreaStyles();

  return <div className={`${classes.root} ${classes.datepicker} ${className}`}>{children}</div>;
};

const TextField: React.FC<{ fee?: boolean } & React.InputHTMLAttributes<HTMLInputElement>> = ({
  fee,
  ...props
}) => <Input {...props} size="small" style={{ width: fee ? '60px' : '50px', margin: 0 }} />;

const EndAdornment: React.FC = ({ children }) => (
  <span style={{ marginLeft: '4px', wordBreak: 'keep-all' }}>{children}</span>
);

const CancelLimitFormItem = observer(CancelLimitFormItemComponent);

export default CancelLimitFormItem;
